import { useState, type ReactNode } from 'react';
import { Alert, Modal, V2Badge, V2Button, V2FormField } from '@sakhtar/design-system/react';
import type {
  GuidedImportColumn,
  GuidedImportPack,
} from '../../lib/dataImport/guidedImportTemplate.js';
import './guidedImportEditor.css';

export type EditorSelection =
  | { kind: 'row'; rowId: string }
  | { kind: 'column'; packId: string; sheetId: string; columnKey: string }
  | null;

export interface GuidedImportReviewRow {
  readonly rowId: string;
  readonly packId: string;
  readonly sheetId: string;
  readonly sourceRow: number;
  readonly values: Readonly<Record<string, unknown>>;
  readonly errors: readonly (string | { field?: string; message: string })[];
}

export interface GuidedImportEditorProps {
  readonly editor: EditorSelection;
  readonly rows: readonly GuidedImportReviewRow[];
  readonly packs: readonly GuidedImportPack[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly saveProgress?: { readonly done: number; readonly total: number } | null;
  readonly onClose: () => void;
  readonly onReload?: () => void;
  readonly onSave: (changes: readonly { rowId: string; values: Record<string, string> }[]) => void;
}

const PAGE_SIZE = 20;
// Keep bulk identity/contact corrections separate from record codes, references and amounts.
const BULK_OPTIONAL_FIELDS = new Set([
  'nationalId',
  'economicCode',
  'postalCode',
  'mobile',
  'phone',
  'email',
]);
type Draft = Record<string, Record<string, string>>;
interface BulkPreview {
  readonly value: string;
  readonly filteredCount: number;
  readonly rows: readonly { rowId: string; sourceRow: number; before: string }[];
}
const optionLabels: Readonly<Record<string, string>> = {
  sales: 'فروش',
  purchase: 'خرید',
  exclusive: 'قیمت بدون مالیات',
  inclusive: 'شامل مالیات',
};

function cellText(value: unknown): string {
  return value === null || value === undefined ? '' : String(value);
}

function fieldErrors(row: GuidedImportReviewRow, key: string): string[] {
  return row.errors.flatMap((error) =>
    typeof error !== 'string' && error.field === key ? [error.message] : [],
  );
}

function rowErrors(row: GuidedImportReviewRow): string[] {
  return row.errors.flatMap((error) =>
    typeof error === 'string' ? [error] : !error.field ? [error.message] : [],
  );
}

/** Uses the shared dialog, including its focus trap and Escape handling. */
export function GuidedImportEditor(props: GuidedImportEditorProps): ReactNode {
  if (!props.editor) return null;
  const editorKey =
    props.editor.kind === 'row'
      ? `row:${props.editor.rowId}`
      : `column:${props.editor.packId}:${props.editor.sheetId}:${props.editor.columnKey}`;
  return <EditorSession key={editorKey} {...props} editor={props.editor} />;
}

function EditorSession({
  editor,
  rows,
  packs,
  busy,
  error,
  saveProgress,
  onClose,
  onReload,
  onSave,
}: GuidedImportEditorProps & { readonly editor: NonNullable<EditorSelection> }): ReactNode {
  const selectedRow = editor.kind === 'row' ? rows.find((row) => row.rowId === editor.rowId) : null;
  const packId = editor.kind === 'column' ? editor.packId : selectedRow?.packId;
  const sheetId = editor.kind === 'column' ? editor.sheetId : selectedRow?.sheetId;
  const pack = packs.find((item) => item.id === packId && item.permitted);
  const sheet = pack?.sheets.find((item) => item.id === sheetId);
  const columns =
    editor.kind === 'column'
      ? (sheet?.columns.filter((column) => column.key === editor.columnKey) ?? [])
      : (sheet?.columns ?? []);
  const matchingRows =
    editor.kind === 'row'
      ? selectedRow && pack && sheet
        ? [selectedRow]
        : []
      : pack && sheet
        ? rows.filter((row) => row.packId === pack.id && row.sheetId === sheet.id)
        : [];
  // Freeze the comparison baseline for this dialog session, including partial-save retries.
  const [original] = useState(
    () =>
      new Map(
        matchingRows.map((row) => [
          row.rowId,
          Object.fromEntries(
            columns.map((column) => [column.key, cellText(row.values[column.key])]),
          ),
        ]),
      ),
  );
  const [draft, setDraft] = useState<Draft>({});
  const [search, setSearch] = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [bulkMode, setBulkMode] = useState<'clear' | 'replace'>('clear');
  const [bulkValue, setBulkValue] = useState('');
  const [bulkPreview, setBulkPreview] = useState<BulkPreview | null>(null);
  const [bulkUndo, setBulkUndo] = useState<Draft | null>(null);
  const bulkColumn = editor.kind === 'column' ? columns[0] : undefined;
  const canBulkEdit =
    bulkColumn &&
    !bulkColumn.required &&
    !bulkColumn.requiredForCommit &&
    BULK_OPTIONAL_FIELDS.has(bulkColumn.key);
  const changes = matchingRows.flatMap((row) => {
    const values = Object.fromEntries(
      columns.flatMap((column) => {
        const value = draft[row.rowId]?.[column.key];
        return value !== undefined && value !== cellText(row.values[column.key])
          ? [[column.key, value]]
          : [];
      }),
    );
    return Object.keys(values).length ? [{ rowId: row.rowId, values }] : [];
  });
  const changedCellCount = changes.reduce(
    (sum, change) => sum + Object.keys(change.values).length,
    0,
  );
  const query = search.trim().toLocaleLowerCase('fa');
  const visibleRows = matchingRows.filter((row) => {
    if (
      errorsOnly &&
      !rowErrors(row).length &&
      !columns.some((column) => fieldErrors(row, column.key).length)
    )
      return false;
    return (
      !query ||
      [
        row.sourceRow,
        ...(sheet?.columns.map((column) => row.values[column.key]) ?? []),
        ...Object.values(draft[row.rowId] ?? {}),
      ].some((value) => cellText(value).toLocaleLowerCase('fa').includes(query))
    );
  });
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = visibleRows.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE);

  function update(rowId: string, key: string, value: string): void {
    setBulkUndo(null);
    setDraft((all) => ({ ...all, [rowId]: { ...all[rowId], [key]: value } }));
  }

  function previewBulk(): void {
    if (!canBulkEdit || !bulkColumn || busy) return;
    const value = bulkMode === 'clear' ? '' : bulkValue;
    if (bulkMode === 'replace' && !value.trim()) return;
    setBulkPreview({
      value,
      filteredCount: visibleRows.length,
      rows: visibleRows.flatMap((row) => {
        const before =
          draft[row.rowId]?.[bulkColumn.key] ?? original.get(row.rowId)?.[bulkColumn.key] ?? '';
        return before === value ? [] : [{ rowId: row.rowId, sourceRow: row.sourceRow, before }];
      }),
    });
  }

  function applyBulk(): void {
    if (!bulkPreview || !bulkColumn || !canBulkEdit || busy) return;
    setBulkUndo(draft);
    setDraft((all) => {
      const next = { ...all };
      for (const row of bulkPreview.rows)
        next[row.rowId] = { ...next[row.rowId], [bulkColumn.key]: bulkPreview.value };
      return next;
    });
    setBulkPreview(null);
  }

  function renderField(row: GuidedImportReviewRow, column: GuidedImportColumn): ReactNode {
    const before = original.get(row.rowId)?.[column.key] ?? '';
    const value = draft[row.rowId]?.[column.key] ?? before;
    const changed = value !== before;
    const messages = fieldErrors(row, column.key);
    const controlId = `guided-edit-${row.rowId}-${column.key}`;
    const options = column.options;
    const requirement = column.requiredForCommit
      ? 'الزامی برای ثبت'
      : column.required
        ? 'الزامی'
        : 'اختیاری؛ قابل خالی‌کردن';
    return (
      <div className="guided-import-editor__field" key={`${row.rowId}:${column.key}`}>
        <V2FormField
          label={column.label}
          required={column.required || column.requiredForCommit === true}
          htmlFor={controlId}
          width="full"
          hint={requirement}
          {...(messages.length
            ? {
                error: `${messages.join('؛ ')}${changed ? ' — پس از ذخیره دوباره بررسی می‌شود.' : ''}`,
              }
            : {})}
        >
          {options?.length ? (
            <select
              id={controlId}
              className="sk-input"
              aria-label={column.label}
              aria-invalid={messages.length > 0}
              value={value}
              disabled={busy || bulkPreview !== null}
              onChange={(event) => update(row.rowId, column.key, event.target.value)}
            >
              <option value="">{column.required ? 'انتخاب کنید' : 'خالی'}</option>
              {value && !options.includes(value) ? (
                <option value={value}>مقدار فعلی: {value}</option>
              ) : null}
              {options.map((option) => (
                <option key={option} value={option}>
                  {optionLabels[option] ?? option}
                </option>
              ))}
            </select>
          ) : (
            <input
              id={controlId}
              className="sk-input"
              aria-label={column.label}
              type="text"
              dir="auto"
              value={value}
              maxLength={10000}
              autoComplete="off"
              spellCheck={false}
              aria-invalid={messages.length > 0}
              disabled={busy || bulkPreview !== null}
              onChange={(event) => update(row.rowId, column.key, event.target.value)}
            />
          )}
        </V2FormField>
        <div className="guided-import-editor__before">
          <span>پیش از اصلاح:</span> <bdi>{before || 'خالی'}</bdi>
          {changed ? <V2Badge tone="info">تغییر کرده</V2Badge> : null}
        </div>
        {changed ? (
          <V2Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy || bulkPreview !== null}
            onClick={() => update(row.rowId, column.key, before)}
          >
            بازگردانی مقدار
          </V2Button>
        ) : null}
      </div>
    );
  }

  const title =
    editor.kind === 'row'
      ? `اصلاح ردیف ${selectedRow?.sourceRow ?? ''}`
      : `اصلاح ستون ${columns[0]?.label ?? ''}`;
  return (
    <div className="guided-import-editor">
      <Modal
        open
        title={title}
        onClose={() => {
          if (!busy) onClose();
        }}
        footer={
          <>
            <span className="guided-import-editor__save-summary" aria-live="polite">
              {busy && saveProgress
                ? `ذخیره/بررسی ${saveProgress.done} از ${saveProgress.total} ردیف`
                : `${changes.length} ردیف برای ذخیره · ${changedCellCount} مقدار تغییر کرده`}
            </span>
            <V2Button type="button" disabled={busy} onClick={onClose}>
              انصراف
            </V2Button>
            <V2Button
              type="button"
              variant="primary"
              disabled={busy || bulkPreview !== null || !changes.length || !pack || !columns.length}
              onClick={() => {
                setBulkUndo(null);
                onSave(changes);
              }}
            >
              {busy ? 'در حال ذخیره…' : `ذخیره اصلاحات ${changes.length} ردیف`}
            </V2Button>
          </>
        }
      >
        <div className="guided-import-editor__content" aria-busy={busy}>
          <p className="guided-import-editor__context">
            {pack?.label} · {sheet?.name}
          </p>
          <Alert tone="info">
            فایل اصلی حفظ می‌شود. فقط مقدارهای تغییرکرده ذخیره می‌شوند؛ ارتباط‌ها و پیش‌نمایش پس از
            اصلاح باید دوباره بررسی شوند.
            {editor.kind === 'column'
              ? ' اصلاح ردیف‌ها به‌ترتیب ذخیره می‌شود؛ اگر عملیات متوقف شود، پیشرفت و ردیف‌های باقی‌مانده در همین پنجره نمایش داده می‌شود.'
              : ''}
          </Alert>
          {error ? (
            <Alert tone="danger">
              <div role="alert">{error}</div>
              {onReload ? (
                <V2Button
                  type="button"
                  size="sm"
                  disabled={busy || bulkPreview !== null}
                  onClick={onReload}
                >
                  دریافت آخرین وضعیت
                </V2Button>
              ) : null}
            </Alert>
          ) : null}
          {!pack || !sheet || !columns.length || !matchingRows.length ? (
            <Alert tone="warning">
              ردیف یا ستون قابل اصلاح در دسترس نیست. اطلاعات و دسترسی این بخش را دوباره بررسی کنید.
            </Alert>
          ) : editor.kind === 'row' ? (
            <>
              {rowErrors(matchingRows[0]!).length ? (
                <Alert tone="warning">{rowErrors(matchingRows[0]!).join('؛ ')}</Alert>
              ) : null}
              <div className="guided-import-editor__fields">
                {columns.map((column) => renderField(matchingRows[0]!, column))}
              </div>
            </>
          ) : (
            <>
              <div className="guided-import-editor__filters">
                <V2FormField label="جست‌وجو در ردیف‌ها" width="wide" htmlFor="guided-edit-search">
                  <input
                    id="guided-edit-search"
                    className="sk-input"
                    type="search"
                    placeholder="مقدار، نام، کد یا شماره ردیف"
                    value={search}
                    disabled={busy || bulkPreview !== null}
                    onChange={(event) => {
                      setSearch(event.target.value);
                      setPage(0);
                    }}
                  />
                </V2FormField>
                <label className="guided-import-editor__checkbox">
                  <input
                    type="checkbox"
                    checked={errorsOnly}
                    disabled={busy || bulkPreview !== null}
                    onChange={(event) => {
                      setErrorsOnly(event.target.checked);
                      setPage(0);
                    }}
                  />
                  فقط ردیف‌های دارای خطای این ستون
                </label>
              </div>
              <p className="guided-import-editor__context">
                {visibleRows.length} ردیف در نتیجه · اصلاحات همه صفحه‌ها تا زمان ذخیره نگه داشته
                می‌شوند.
              </p>
              {canBulkEdit && bulkColumn ? (
                <section className="guided-import-editor__bulk" aria-label="اصلاح دسته‌ای ستون">
                  <strong>اصلاح دسته‌ای {bulkColumn.label}</strong>
                  <p className="guided-import-editor__context">
                    فقط همین ستون در {visibleRows.length} ردیف مطابق فیلتر، در همهٔ صفحه‌ها و همین
                    شیت بررسی می‌شود. فایل اصلی تغییر نمی‌کند.
                  </p>
                  {bulkPreview ? (
                    <div role="status" className="guided-import-editor__bulk-preview">
                      <p>
                        پیش‌نمایش: از {bulkPreview.filteredCount} ردیف مطابق فیلتر، مقدار{' '}
                        {bulkColumn.label} در {bulkPreview.rows.length} ردیف{' '}
                        {bulkPreview.value === '' ? 'خالی می‌شود' : 'جایگزین می‌شود'}.
                      </p>
                      <p>
                        مقدار جدید: <bdi>{bulkPreview.value || 'خالی'}</bdi>
                      </p>
                      <ul>
                        {bulkPreview.rows.slice(0, 5).map((row) => (
                          <li key={row.rowId}>
                            ردیف {row.sourceRow}: <bdi>{row.before || 'خالی'}</bdi> ←{' '}
                            <bdi>{bulkPreview.value || 'خالی'}</bdi>
                          </li>
                        ))}
                      </ul>
                      {bulkPreview.rows.length > 5 ? (
                        <p>و {bulkPreview.rows.length - 5} ردیف دیگر در همین فیلتر.</p>
                      ) : null}
                      <p>این تأیید فقط پیش‌نویس را تغییر می‌دهد؛ ذخیره و اعتبارسنجی جداگانه است.</p>
                      <div className="guided-import-editor__bulk-actions">
                        <V2Button
                          type="button"
                          disabled={busy}
                          onClick={() => setBulkPreview(null)}
                        >
                          لغو پیش‌نمایش
                        </V2Button>
                        <V2Button
                          type="button"
                          variant="primary"
                          disabled={busy || !bulkPreview.rows.length}
                          onClick={applyBulk}
                        >
                          تأیید تغییر {bulkPreview.rows.length} ردیف در پیش‌نویس
                        </V2Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="guided-import-editor__bulk-actions">
                        <V2FormField label="عملیات دسته‌ای" htmlFor="guided-bulk-mode">
                          <select
                            id="guided-bulk-mode"
                            className="sk-input"
                            value={bulkMode}
                            disabled={busy}
                            onChange={(event) =>
                              setBulkMode(event.target.value === 'replace' ? 'replace' : 'clear')
                            }
                          >
                            <option value="clear">خالی‌کردن مقادیر</option>
                            <option value="replace">جایگزینی با مقدار مشخص</option>
                          </select>
                        </V2FormField>
                        {bulkMode === 'replace' ? (
                          <V2FormField label="مقدار جایگزین" htmlFor="guided-bulk-value">
                            <input
                              id="guided-bulk-value"
                              className="sk-input"
                              type="text"
                              dir="auto"
                              maxLength={10000}
                              value={bulkValue}
                              disabled={busy}
                              onChange={(event) => setBulkValue(event.target.value)}
                            />
                          </V2FormField>
                        ) : null}
                        <V2Button
                          type="button"
                          disabled={
                            busy ||
                            !visibleRows.length ||
                            (bulkMode === 'replace' && !bulkValue.trim())
                          }
                          onClick={previewBulk}
                        >
                          پیش‌نمایش اصلاح دسته‌ای
                        </V2Button>
                      </div>
                      {bulkUndo ? (
                        <V2Button
                          type="button"
                          variant="ghost"
                          disabled={busy}
                          onClick={() => {
                            setDraft(bulkUndo);
                            setBulkUndo(null);
                          }}
                        >
                          بازگردانی آخرین اصلاح دسته‌ای
                        </V2Button>
                      ) : null}
                    </>
                  )}
                </section>
              ) : null}
              <div className="guided-import-editor__column-rows">
                {pageRows.map((row) => (
                  <section
                    key={row.rowId}
                    className="guided-import-editor__column-row"
                    aria-label={`ردیف ${row.sourceRow}`}
                  >
                    <div className="guided-import-editor__row-label">
                      <strong>ردیف {row.sourceRow}</strong>
                      <bdi>
                        {cellText(
                          sheet.columns.some((column) => column.key === 'code')
                            ? row.values['code']
                            : sheet.columns.some((column) => column.key === 'name')
                              ? row.values['name']
                              : '',
                        )}
                      </bdi>
                      {rowErrors(row).length ? (
                        <span className="guided-import-editor__row-error">
                          {rowErrors(row).join('؛ ')}
                        </span>
                      ) : null}
                    </div>
                    {renderField(row, columns[0]!)}
                  </section>
                ))}
              </div>
              {!visibleRows.length ? <p role="status">ردیفی مطابق این فیلتر پیدا نشد.</p> : null}
              <nav className="guided-import-editor__pagination" aria-label="صفحه‌های اصلاح ستون">
                <V2Button
                  type="button"
                  size="sm"
                  disabled={busy || currentPage === 0}
                  onClick={() => setPage(currentPage - 1)}
                >
                  قبلی
                </V2Button>
                <span>
                  صفحه {currentPage + 1} از {pageCount}
                </span>
                <V2Button
                  type="button"
                  size="sm"
                  disabled={busy || currentPage + 1 >= pageCount}
                  onClick={() => setPage(currentPage + 1)}
                >
                  بعدی
                </V2Button>
              </nav>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
